import { useState } from 'react'

interface AssociationCounts {
  projects: number
  tasks: number
  users: number
}

interface UseWorkspaceChangeOptions {
  onConfirm: (newWorkspaceId: string) => void
  currentWorkspaceId?: string
  checkChange?: boolean
}

/**
 * Hook to manage workspace change confirmation workflow
 * Handles showing a confirmation dialog when there are existing associations
 */
export function useWorkspaceChange({
  onConfirm,
  currentWorkspaceId,
  checkChange = false,
}: UseWorkspaceChangeOptions) {
  const [showDialog, setShowDialog] = useState(false)
  const [pendingWorkspaceId, setPendingWorkspaceId] = useState<string | null>(null)

  const handleWorkspaceChange = (newWorkspaceId: string, associationCounts: AssociationCounts) => {
    // Check if this is actually a change from the current workspace
    if (checkChange && currentWorkspaceId && newWorkspaceId === currentWorkspaceId) {
      // Not actually changing, proceed directly
      onConfirm(newWorkspaceId)
      return
    }

    // Check if there are any associations that would be affected
    const hasAssociations =
      associationCounts.projects > 0 || associationCounts.tasks > 0 || associationCounts.users > 0

    if (hasAssociations && (!checkChange || currentWorkspaceId)) {
      // Show confirmation dialog
      setPendingWorkspaceId(newWorkspaceId)
      setShowDialog(true)
    } else {
      // No associations to lose, proceed directly
      onConfirm(newWorkspaceId)
    }
  }

  const confirmChange = () => {
    if (pendingWorkspaceId) {
      onConfirm(pendingWorkspaceId)
      setShowDialog(false)
      setPendingWorkspaceId(null)
    }
  }

  const cancelChange = () => {
    setShowDialog(false)
    setPendingWorkspaceId(null)
  }

  return {
    showDialog,
    handleWorkspaceChange,
    confirmChange,
    cancelChange,
  }
}
